/**
 * Football pitch showing weighted average attack positions over a time range.
 * Only shows teammates of the selected player.
 * Selected player = orange, others = white/semi-transparent.
 * Position role label shown under each dot.
 * Mini-timeline bar at bottom shows formation runs + current viewing range.
 *
 * Coordinate mapping (matching frontend-lk Pitch):
 *   field x (0..fieldWidth)  → SVG Y (inverted: bottom=0, top=fieldWidth)
 *   field y (0..fieldHeight) → SVG X (left=0, right=fieldHeight)
 *
 * fieldSize.width  = short side of the pitch (e.g. 6000)
 * fieldSize.height = long side of the pitch  (e.g. 10000)
 */

const PITCH_W = 700;
const PITCH_H = 450;
const PAD = 30;
const FIELD_W = PITCH_W - PAD * 2;
const FIELD_H = PITCH_H - PAD * 2;
const TIMELINE_H = 24;
const TIMELINE_GAP = 8;

const HIGHLIGHT = "#f59e0b";
const TEAMMATE_FILL = "rgba(255,255,255,0.5)";
const TEAMMATE_STROKE = "rgba(255,255,255,0.8)";

const RUN_COLORS = ["#3b82f6", "#8b5cf6", "#06b6d4", "#10b981", "#ec4899", "#f97316"];

function formatPosShort(name, side) {
  if (!name) return "";
  const initials = name
    .split("_")
    .map((w) => w[0].toUpperCase())
    .join("");
  if (!side || side === "center") return initials;
  return side[0].toUpperCase() + initials;
}

/**
 * Map field coordinates to SVG pixel coordinates (horizontal pitch).
 */
function toPixel(fieldX, fieldY, fw, fh) {
  const goalLine = Math.max(fw, fh);
  const sideLine = Math.min(fw, fh);
  const svgX = PAD + (fieldX / goalLine) * FIELD_W;
  const svgY = PAD + (fieldY / sideLine) * FIELD_H;
  return [svgX, svgY];
}

/**
 * Average positions across multiple 5-min windows for a given phase.
 */
const WINDOW_MIN = 1;
function averagePositions(teamWeighted, startMinute, endMinute, phase) {
  const startWin = Math.max(0, Math.floor((startMinute - 1) / WINDOW_MIN));
  const endWin = Math.max(startWin, Math.floor((endMinute - 1) / WINDOW_MIN));

  const phases = phase === "common" ? ["attack", "defense"] : [phase || "attack"];

  const sums = {};
  for (let w = startWin; w <= endWin; w++) {
    const winData = teamWeighted[String(w)] || teamWeighted[w];
    if (!winData) continue;
    for (const ph of phases) {
      const phData = winData[ph] || {};
      for (const [uid, coords] of Object.entries(phData)) {
        if (!coords || coords[0] == null || coords[1] == null) continue;
        if (!sums[uid]) sums[uid] = { x: 0, y: 0, count: 0 };
        sums[uid].x += coords[0];
        sums[uid].y += coords[1];
        sums[uid].count += 1;
      }
    }
  }

  const result = {};
  for (const [uid, s] of Object.entries(sums)) {
    result[uid] = [s.x / s.count, s.y / s.count];
  }
  return result;
}

const PositionPitch = ({
  supertracks,
  selectedUid,
  weightedPositions,
  fieldSize,
  startMinute,
  endMinute,
  getStartPosition,
  phase,
  dynamicWindows,
  formationRuns,
  matchTimeSec,
  viewStartSec,
  viewEndSec,
}) => {
  if (!supertracks || !fieldSize) return null;

  const fw = fieldSize.width || 6000;
  const fh = fieldSize.height || 10000;

  const selectedSt = supertracks.find((s) => s.super_track_uid === selectedUid);
  const teamKind = selectedSt?.kind;

  const dots = [];
  if (teamKind && weightedPositions) {
    const teamWeighted = weightedPositions[teamKind] || {};
    const avgPositions = averagePositions(teamWeighted, startMinute || 1, endMinute || 5, phase || "attack");
    const teammates = supertracks.filter((s) => s.kind === teamKind);

    for (const st of teammates) {
      const uid = String(st.super_track_uid);
      const coords = avgPositions[uid];
      if (!coords) continue;

      const [cx, cy] = toPixel(coords[0], coords[1], fw, fh);
      const isSelected = st.super_track_uid === selectedUid;
      const pos = getStartPosition ? getStartPosition(st.super_track_uid) : null;
      const posLabel = pos ? formatPosShort(pos.name, pos.side) : "";

      dots.push({ cx, cy, isSelected, jersey: st.jersey, uid: st.super_track_uid, posLabel });
    }
  }

  const hasTimeline = matchTimeSec > 0 && (dynamicWindows?.length > 0 || formationRuns?.length > 0);
  const totalH = PITCH_H + (hasTimeline ? TIMELINE_GAP + TIMELINE_H : 0);
  const tlY = PITCH_H + TIMELINE_GAP;
  const totalSec = matchTimeSec || 2700;

  return (
    <svg
      viewBox={`0 0 ${PITCH_W} ${totalH}`}
      style={{ width: "100%", background: "#1a472a", borderRadius: 8 }}
    >
      <rect x={PAD} y={PAD} width={FIELD_W} height={FIELD_H}
        fill="none" stroke="#3d8b37" strokeWidth={2} />
      <line x1={PITCH_W / 2} y1={PAD} x2={PITCH_W / 2} y2={PITCH_H - PAD}
        stroke="#3d8b37" strokeWidth={2} />
      <circle cx={PITCH_W / 2} cy={PITCH_H / 2} r={55}
        fill="none" stroke="#3d8b37" strokeWidth={2} />
      <circle cx={PITCH_W / 2} cy={PITCH_H / 2} r={3} fill="#3d8b37" />
      <rect x={PAD} y={PITCH_H / 2 - 100} width={100} height={200}
        fill="none" stroke="#3d8b37" strokeWidth={2} />
      <rect x={PAD} y={PITCH_H / 2 - 45} width={36} height={90}
        fill="none" stroke="#3d8b37" strokeWidth={2} />
      <rect x={PITCH_W - PAD - 100} y={PITCH_H / 2 - 100} width={100} height={200}
        fill="none" stroke="#3d8b37" strokeWidth={2} />
      <rect x={PITCH_W - PAD - 36} y={PITCH_H / 2 - 45} width={36} height={90}
        fill="none" stroke="#3d8b37" strokeWidth={2} />

      {/* Non-selected teammates */}
      {dots.filter((d) => !d.isSelected).map((d) => (
        <g key={d.uid}>
          <circle cx={d.cx} cy={d.cy} r={14}
            fill={TEAMMATE_FILL} stroke={TEAMMATE_STROKE} strokeWidth={1.5} />
          <text x={d.cx} y={d.cy + 1} textAnchor="middle" dominantBaseline="central"
            fontSize={10} fontWeight={600} fill="#fff">
            {d.jersey ?? ""}
          </text>
          {d.posLabel && (
            <text x={d.cx} y={d.cy + 22} textAnchor="middle"
              fontSize={8} fill="rgba(255,255,255,0.7)">
              {d.posLabel}
            </text>
          )}
        </g>
      ))}

      {/* Selected player */}
      {dots.filter((d) => d.isSelected).map((d) => (
        <g key={d.uid}>
          <circle cx={d.cx} cy={d.cy} r={16}
            fill={HIGHLIGHT} stroke="#fff" strokeWidth={2} />
          <text x={d.cx} y={d.cy + 1} textAnchor="middle" dominantBaseline="central"
            fontSize={11} fontWeight={700} fill="#fff">
            {d.jersey ?? ""}
          </text>
          {d.posLabel && (
            <text x={d.cx} y={d.cy + 24} textAnchor="middle"
              fontSize={9} fontWeight={600} fill="#fff">
              {d.posLabel}
            </text>
          )}
        </g>
      ))}

      {/* Mini-timeline bar */}
      {hasTimeline && (
        <g transform={`translate(${PAD}, ${tlY})`}>
          <rect x={0} y={0} width={FIELD_W} height={TIMELINE_H}
            fill="rgba(0,0,0,0.4)" rx={3} />

          {/* Formation run segments */}
          {(formationRuns || []).map((run, i) => {
            const x = (run.start_sec / totalSec) * FIELD_W;
            const w = Math.max(1, ((run.end_sec - run.start_sec) / totalSec) * FIELD_W);
            return (
              <g key={`run-${i}`}>
                <rect x={x} y={1} width={w} height={TIMELINE_H - 2}
                  fill={RUN_COLORS[i % RUN_COLORS.length]} opacity={0.4} rx={2} />
                {w > 45 && (
                  <text x={x + w / 2} y={TIMELINE_H / 2 + 1} textAnchor="middle"
                    dominantBaseline="central" fontSize={11} fill="rgba(255,255,255,0.9)"
                    fontWeight={600}>
                    {run.formation}
                  </text>
                )}
              </g>
            );
          })}

          {/* Dynamic window boundary ticks */}
          {(dynamicWindows || []).map((w, i) => {
            const x = (w.startSec / totalSec) * FIELD_W;
            return (
              <line key={`tick-${i}`} x1={x} y1={0} x2={x} y2={TIMELINE_H}
                stroke="rgba(255,255,255,0.5)" strokeWidth={1} />
            );
          })}

          {/* Current viewing range highlight */}
          {viewStartSec != null && viewEndSec != null && (() => {
            const x = (viewStartSec / totalSec) * FIELD_W;
            const w = Math.max(2, ((viewEndSec - viewStartSec) / totalSec) * FIELD_W);
            return (
              <rect x={x} y={0} width={w} height={TIMELINE_H}
                fill="rgba(245,158,11,0.4)" stroke="#f59e0b" strokeWidth={1.5} rx={2} />
            );
          })()}
        </g>
      )}
    </svg>
  );
};

export default PositionPitch;
